import ApiErrorBanner from '@/components/ApiErrorBanner';
import Button from '@/components/UI/Button';
import DatePickerNative from '@/components/UI/DatePickerNative';
import { toNumericId } from '@/helpers';
import { _isEmpty } from '@/littledash';
import type { Animal } from '@/model/Animal.model';
import type { StudyApiId } from '@/model/Study.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { DateUtils } from '@/utils/Date.utils';
import { useEntitySchemaFieldsByType } from '@/utils/entitySchema/useEntitySchema';
import { type FC, type FormEvent, useState } from 'react';

interface UpdateDOBProps {
  subjects: Array<Animal>;
  studyApiId: StudyApiId;
  closeModal: () => void;
  handleCallback: (message: string) => void;
}

const UpdateDOB: FC<UpdateDOBProps> = ({ subjects, studyApiId, closeModal, handleCallback }) => {
  const dateNowIso = DateUtils.dateNow();
  const entityFieldRequiredMap = useEntitySchemaFieldsByType('animal');
  const isDobRequired = entityFieldRequiredMap?.dob ?? true;

  const [date, setDate] = useState(dateNowIso);
  const [validationError, setValidationError] = useState('');

  const {
    invoke: updateDob,
    loading,
    response: updateResponse,
  } = useApiHook({
    endpoint: 'PATCH /api/v1/studies/{studyApiId}/animals/dob',
    invokeOnInit: false,
    options: { onError: { throw: false, capture: true, toast: false, slug: 'animal-dob-update' } },
  });

  const submitForm = async (e: FormEvent) => {
    e.preventDefault();

    if (isDobRequired && _isEmpty(date)) {
      setValidationError('DOB is required');
      return;
    }

    setValidationError('');
    const animalIds = subjects.map((s) => toNumericId(s.id));
    const result = await updateDob({
      path: { studyApiId },
      body: { animal_ids: animalIds, dob: _isEmpty(date) ? null : date },
    });
    if (result.type === 'success') {
      handleCallback('Successfully updated date of birth');
    }
  };

  const what = subjects.length > 1 ? 'animals' : 'animal';
  const whatDemonstrative = what === 'animals' ? 'these' : 'this';
  const message = `Update the date of birth for ${whatDemonstrative} ${what}?`;

  return (
    <form onSubmit={submitForm} className="ui__narrow-modal">
      <div className="mb3 tc">
        <h3 className="normal f4 pb2">{message}</h3>
      </div>
      {updateResponse?.type === 'error' && <ApiErrorBanner error={updateResponse.error} />}
      <div className="mb4">
        <label htmlFor="dob">Date of birth</label>
        {validationError && <p className="red f6 mt2 mb0">{validationError}</p>}
        <DatePickerNative
          value={date}
          onChange={(value: string) => {
            setDate(value);
            setValidationError('');
          }}
          max={dateNowIso}
          style={{ marginBottom: 0 }}
        />
      </div>
      <div>
        <Button submit disabled={loading} className="w-100 mb2">
          Save
        </Button>
        <Button plain className="w-100" onClick={() => closeModal()}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default UpdateDOB;
